import { queryClient } from "../lib/queryClient";
import { auth } from "../firebaseConfig";
import { useDroneMapStore } from "../stores/droneMapStore";
import { useEncryptedRangeStore } from "../stores/encryptedRangeStore";
import { useActiveEncryptedDialogStore } from "../stores/activeEncryptedDialogStore";
import { fetchLogout, fetchLogoutEverywhere } from "../APIClient";
import { resetDroneNotificationDedup } from "../utils/notificationUtils";
import { scanningClient } from "../realtime/scanningClient";
import { clearLastAirPlateMessages } from "../realtime/airplateMessageCache";
import { broadcastSessionChange } from "./sessionSync";

// User-scoped storage cleared on logout.
// PRESERVED (device-scoped preferences): theme, notificationSoundEnabled.
const LS_KEYS = [
  "accessToken",
  "liveDroneLastSeen",
  "bellNotifications",
  // Which flights the user dismissed from the bell. User-scoped: the next
  // account signing in on this device must not inherit them.
  "bellDismissedSessions",
  "configTab",
  // Legacy flat key, superseded by the org-namespaced `notificationEnabled:<orgId>`
  // (swept via LS_PREFIXES below). Removed here so pre-migration values don't linger.
  "notificationEnabled",
];

// Org-scoped keys that use a runtime suffix (e.g.
// `encryptedRangePreference:<orgId>`, `notificationEnabled:<orgId>`). Swept by
// prefix on logout so a re-login as a different org doesn't inherit the
// previous org's preference.
const LS_PREFIXES = [
  "encryptedRangePreference:",
  "notificationEnabled:",
];

const SS_KEYS = [
  "accessToken",
  "2fa_verified",
  "droneFirstSeen",
  "droneNotifiedKeys",
];

function removeByPrefix(prefixes: string[]): void {
  const matches: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && prefixes.some((p) => key.startsWith(p))) matches.push(key);
  }
  for (const key of matches) localStorage.removeItem(key);
}

export async function performLogout(
  navigateFn?: (path: string) => void,
  destination = "/login",
  // When `everywhere: true`, server-side revokes the refresh token for the
  // whole user (not just this device), so any other browser/device gets
  // 401 within ≤30s and is forced back to /login. Local cleanup below is
  // identical regardless of scope.
  options?: { everywhere?: boolean },
): Promise<void> {
  // Escape floor. A wedged session — e.g. you switched to an org you have no
  // membership on — must NEVER trap you on a blank screen with a dead Logout.
  // If the cleanup below hangs (a stuck query never cancelling) OR the SPA
  // navigate is trapped by a broken render, hard-redirect after a short delay.
  // The path check skips the reload when the SPA navigate already moved us
  // somewhere safe — the common, happy path — so a normal logout is unaffected.
  const escapeFloor = window.setTimeout(() => {
    const path = window.location.pathname;
    if (path === destination || path.startsWith(destination)) return; // SPA nav worked
    try {
      window.location.assign(destination);
    } catch {
      /* last resort: nothing else we can do */
    }
  }, 1500);

  try { scanningClient.stop(); } catch {}

  try {
    if (options?.everywhere) {
      await fetchLogoutEverywhere();
    } else {
      await fetchLogout();
    }
  } catch {}
  try { await auth.signOut(); } catch {}

  // Cancel in-flight requests so they cannot repopulate the cache after clear.
  // Guarded: on a wedged session a query whose cancellation never resolves
  // must not block the navigate below — that was the dead-Logout cause.
  try { await queryClient.cancelQueries(); } catch {}
  try { queryClient.clear(); } catch {}

  // Each cleanup step is independently guarded so one throwing can't stop the
  // navigate (and therefore the logout) from happening.
  try {
    for (const key of LS_KEYS) localStorage.removeItem(key);
    removeByPrefix(LS_PREFIXES);
    for (const key of SS_KEYS) sessionStorage.removeItem(key);
  } catch {}

  try { useDroneMapStore.getState().reset(); } catch {}
  try { useEncryptedRangeStore.getState().reset(); } catch {}
  // The open encrypted dialog's session id. User-scoped: left set, the next
  // account signing in on this device would have the live listener
  // invalidating the previous session's per-id queries.
  try { useActiveEncryptedDialogStore.getState().setId(null); } catch {}
  try { resetDroneNotificationDedup(); } catch {}
  try { clearLastAirPlateMessages(); } catch {}

  // Cross-tab logout: tell other same-origin tabs to reload so the auth guard
  // bounces them to /login too — the logout analog of the org-switch reload.
  // (Cross-browser/device logout is the `everywhere` server-side revoke above,
  // which makes other sessions 401 within ~30s.)
  try { broadcastSessionChange("logout"); } catch {}

  // When the destination route mounts, its useQuery hooks immediately register
  // new entries in the cache (even enabled:false queries appear in getAll()).
  // Patch QueryCache.add to no-op for 150 ms so that newly mounted components
  // cannot populate the cache during the logout window.  After 150 ms the
  // patch is reverted and queued queries will retry on their next render cycle.
  // 150 ms covers: React render+commit+effect (~few rAF ticks) plus Playwright's
  // CDP round-trip for the post-logout snapshotCache evaluate (~10–50 ms).
  const qCache = queryClient.getQueryCache();
  const origAdd = (qCache as unknown as { add: (q: unknown) => void }).add.bind(qCache);
  (qCache as unknown as { add: (q: unknown) => void }).add = () => { /* blocked during logout */ };
  navigateFn?.(destination);
  setTimeout(() => {
    (qCache as unknown as { add: (q: unknown) => void }).add = origAdd;
  }, 150);
}
